import React, { useEffect, useMemo, useState } from "react";
import LeafL from "../Images/Konn3ct Images/leaf left.png";
import LeafR from "../Images/Konn3ct Images/right leaf.png";
import { motion } from "framer-motion";
import { Video, Users, HardDrive, Sparkles, UserPlus } from "lucide-react";
import { useCountry } from "../Context/CountryContext";
import { formatCurrency } from "../Utils/formatCurrency";
import { trackEvent } from "../Analytics/analytics";
import { fetchCommercialPlans } from "./pricingService";
import { getPricingForCountry } from "./pricingConfig";

const REGISTER_URL = "https://dashboard.konn3ct.ng/register";
const ADDONS_URL = "https://dashboard.konn3ct.ng/addons";
const SALES_URL = "https://www.newwavesecosystem.com/contactus";

const openExternal = (url) => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isEnterprisePlan = (plan) => {
  const name = normalize(plan?.name);
  return name.includes("enterprise") || name.includes("business");
};

const isFreePlan = (plan) => {
  const name = normalize(plan?.name);

  if (name.includes("free") || name.includes("basic")) {
    return true;
  }

  const monthly = Number(plan?.monthly_cost);
  const yearly = Number(plan?.yearly_cost);

  return Number.isFinite(monthly) && Number.isFinite(yearly) && monthly === 0 && yearly === 0;
};

const isMostPopularPlan = (plan) => {
  const name = normalize(plan?.name);
  return name === "professional" || name === "pro";
};

const getPlanDescription = (plan) => {
  const name = normalize(plan?.name);

  if (name.includes("free") || name.includes("basic")) {
    return "Built for individuals and first-time users";
  }

  if (name.includes("lite") || name.includes("starter")) {
    return "Built for tutors, consultants, and small teams";
  }

  if (name === "professional" || name === "pro") {
    return "Built for trainers, creators, churches, and SMEs";
  }

  if (name.includes("event")) {
    return "Built for paid events, webinars, and large audience experiences";
  }

  if (name.includes("business")) {
    return "Built for growing teams and organizations";
  }

  if (name.includes("enterprise")) {
    return "Built for banks, schools, government, and regulated institutions";
  }

  return "Flexible Konn3ct plan for your meetings and collaboration needs";
};

export const isItemDisabled = (item) => {
  const status = normalize(item?.status);
  return status === "inactive" || status === "disabled" || status === "0";
};

export const isCustomPriceString = (value) => {
  const raw = normalize(String(value ?? "").split("/")[0]);
  if (!raw) return false;
  if (raw.includes("custom")) return true;
  return Number.isNaN(Number(raw));
};

export const getPlanPrice = (plan, billing) => {
  if (isFreePlan(plan)) {
    return {
      amount: 0,
      isFree: true,
      isCustom: false,
      available: true,
      pricing: null,
    };
  }

  const planPricings = Array.isArray(plan?.plan_pricings) ? plan.plan_pricings : [];

  const matchedPricing = planPricings.find(
    (pricing) =>
      normalize(pricing?.type) === billing && (pricing?.status === undefined || Number(pricing.status) !== 0),
  );

  if (
    matchedPricing &&
    matchedPricing.price !== undefined &&
    matchedPricing.price !== null &&
    matchedPricing.price !== ""
  ) {
    if (isCustomPriceString(matchedPricing.price)) {
      return {
        amount: null,
        isFree: false,
        isCustom: true,
        available: false,
        pricing: matchedPricing,
      };
    }

    const amount = Number(matchedPricing.price);

    if (Number.isFinite(amount)) {
      return {
        amount,
        isFree: amount === 0,
        isCustom: false,
        available: true,
        pricing: matchedPricing,
      };
    }
  }

  const fallbackField = billing === "yearly" ? plan?.yearly_cost : plan?.monthly_cost;

  if (fallbackField !== undefined && fallbackField !== null && fallbackField !== "") {
    if (isCustomPriceString(fallbackField)) {
      return {
        amount: null,
        isFree: false,
        isCustom: true,
        available: false,
        pricing: null,
      };
    }

    const amount = Number(fallbackField);

    if (Number.isFinite(amount)) {
      return {
        amount,
        isFree: amount === 0,
        isCustom: false,
        available: true,
        pricing: null,
      };
    }
  }

  return {
    amount: null,
    isFree: false,
    isCustom: false,
    available: false,
    pricing: null,
  };
};

const FeatureList = ({ features = [] }) => {
  if (!Array.isArray(features) || features.length === 0) {
    return (
      <p className="border-t border-dashed pt-4 text-sm text-gray-500">
        Feature information is not currently available for this plan.
      </p>
    );
  }

  return (
    <ul className="space-y-2 text-sm text-gray-700 border-t border-dashed pt-4">
      {features.map((item, index) => (
        <li key={`${item}-${index}`} className="flex items-start">
          <span className="text-blue-600 mr-2" aria-hidden="true">
            ✔
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const getAddonIcon = (addon) => {
  const name = normalize(addon?.name);

  if (name.includes("seat") || name.includes("host")) return UserPlus;
  if (name.includes("capacity") || name.includes("participant")) return Users;
  if (name.includes("storage") || name.includes("recording")) return HardDrive;
  if (name.includes("stream") || name.includes("replay") || name.includes("bandwidth")) {
    return Video;
  }

  return Sparkles;
};

const getAddonKey = (addon) => {
  const name = normalize(addon?.name);
  const slug = normalize(addon?.slug || addon?.code || addon?.key);

  const haystack = `${name} ${slug}`;

  if (haystack.includes("seat") || haystack.includes("host")) return "seats";
  if (haystack.includes("capacity") || haystack.includes("participant") || haystack.includes("large")) {
    return "capacity";
  }
  if (haystack.includes("storage") || haystack.includes("recording")) return "storage";
  if (haystack.includes("stream") || haystack.includes("replay") || haystack.includes("bandwidth")) {
    return "streaming";
  }
  if (haystack.includes("ai") || haystack.includes("credit")) return "ai_credits";

  return null;
};

const Kpricing = ({ onSelectAddOn } = {}) => {
  const { country } = useCountry();
  const countryPricing = useMemo(() => getPricingForCountry(country?.code), [country?.code]);

  const [billing, setBilling] = useState("yearly");
  const [activePlan, setActivePlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [currency, setCurrency] = useState(country?.currency || "USD");
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");

  useEffect(() => {
    let isMounted = true;
    let activeController = null;

    const loadPlans = async ({ silent = false, forceRefresh = false } = {}) => {
      if (activeController) {
        activeController.abort();
      }
      const controller = new AbortController();
      activeController = controller;

      const targetCurrency = country?.currency || "USD";

      if (!silent) {
        setPlansLoading(true);
        setPlansError("");
      }
      setCurrency(targetCurrency);

      try {
        const data = await fetchCommercialPlans(targetCurrency, {
          signal: controller.signal,
          forceRefresh,
        });

        if (!isMounted) return;

        const apiPlans = (Array.isArray(data?.plans) ? data.plans : []).filter(
          (plan) => !isItemDisabled(plan),
        );
        const apiAddons = (Array.isArray(data?.addons) ? data.addons : []).filter(
          (addon) => !isItemDisabled(addon),
        );

        setPlans(apiPlans);
        setAddons(apiAddons);
        setCurrency(data?.currency || targetCurrency);
        setCurrencySymbol(data?.currency_symbol || "");

        setActivePlan((current) => {
          if (current && apiPlans.some((plan) => String(plan.id ?? plan.name) === String(current))) {
            return current;
          }

          const preferred = apiPlans.find((plan) => isMostPopularPlan(plan)) || apiPlans[0];

          return preferred ? String(preferred.id ?? preferred.name) : null;
        });
      } catch (error) {
        if (!isMounted || error?.name === "AbortError") return;

        console.error("Unable to load commercial plans:", error);
        if (!silent) {
          setPlans([]);
          setAddons([]);
          setPlansError(error?.message || "Pricing is temporarily unavailable. Please try again.");
        }
      } finally {
        if (isMounted && !silent) {
          setPlansLoading(false);
        }
      }
    };

    loadPlans({ silent: false, forceRefresh: true });

    const handleFocusOrVisibility = () => {
      if (document.visibilityState === "visible") {
        loadPlans({ silent: true, forceRefresh: true });
      }
    };

    window.addEventListener("focus", handleFocusOrVisibility);
    document.addEventListener("visibilitychange", handleFocusOrVisibility);

    return () => {
      isMounted = false;
      if (activeController) {
        activeController.abort();
      }
      window.removeEventListener("focus", handleFocusOrVisibility);
      document.removeEventListener("visibilitychange", handleFocusOrVisibility);
    };
  }, [country?.code, country?.currency]);

  const planPriceMap = useMemo(() => {
    const map = new Map();

    plans.forEach((plan) => {
      map.set(String(plan.id ?? plan.name), getPlanPrice(plan, billing));
    });

    return map;
  }, [billing, plans]);

  const formatPlanAmount = (amount) =>
    formatCurrency({
      amount,
      currency,
      locale: country.locale,
    });

  const resolveAddonPrice = (addon) => {
    const addonKey = getAddonKey(addon);

    // 1. First choice: API currency-specific price structures from backend database (`addon_pricings`)
    const pricingArrays = [addon?.addon_pricings, addon?.pricings, addon?.prices, addon?.country_pricings].filter(
      Array.isArray,
    );

    for (const pricingList of pricingArrays) {
      const match = pricingList.find(
        (entry) =>
          normalize(entry?.currency) === normalize(currency) &&
          (entry?.status === undefined || Number(entry.status) !== 0),
      );

      const candidate = Number(match?.price ?? match?.amount);
      if (match && Number.isFinite(candidate)) {
        return {
          amount: candidate,
          currency: match.currency || currency,
          locale: country.locale,
          source: "api-currency-pricing",
          addonKey,
          pricing: match,
        };
      }
    }

    // 2. Second choice: API direct price localized by CommercialController@plans
    const apiAmount = Number(addon?.price ?? addon?.amount);
    if (Number.isFinite(apiAmount)) {
      return {
        amount: apiAmount,
        currency: addon?.currency || currency || countryPricing?.currency || "USD",
        locale: country.locale,
        source: "api-direct",
        addonKey,
      };
    }

    // 3. Fallback: static country config (`pricingConfig.js`)
    const configuredAmount = addonKey && countryPricing?.addons ? Number(countryPricing.addons[addonKey]) : NaN;
    if (Number.isFinite(configuredAmount)) {
      return {
        amount: configuredAmount,
        currency: countryPricing.currency || currency,
        locale: countryPricing.locale || country.locale,
        source: "country-config",
        addonKey,
      };
    }

    return {
      amount: null,
      currency: countryPricing?.currency || currency || "USD",
      locale: countryPricing?.locale || country.locale,
      source: "unavailable",
      addonKey,
    };
  };

  const handleBillingChange = (cycle) => {
    setBilling(cycle);

    trackEvent("pricing_billing_toggle", {
      country: country.code,
      currency,
      billing: cycle,
    });
  };

  const handlePlanClick = (plan) => {
    const planKey = String(plan.id ?? plan.name);
    setActivePlan(planKey);

    const price = getPlanPrice(plan, billing);

    trackEvent("pricing_plan_selected", {
      country: country.code,
      currency,
      billing,
      plan: plan.name,
      plan_id: plan.id,
      plan_code: price.pricing?.plan_code,
      price: price.available ? price.amount : undefined,
    });
  };

  const handlePlanCta = (plan) => {
    const price = getPlanPrice(plan, billing);
    const enterprise =
      Boolean(price.isCustom) || (isEnterprisePlan(plan) && !price.available);

    trackEvent("pricing_cta_clicked", {
      country: country.code,
      currency,
      billing,
      plan: plan.name,
      plan_id: plan.id,
      plan_code: price.pricing?.plan_code,
      price: price.available ? price.amount : undefined,
      target: enterprise ? "contact_sales" : "register",
    });

    openExternal(enterprise ? SALES_URL : REGISTER_URL);
  };

  const handleAddonCta = (addon) => {
    const addonPrice = resolveAddonPrice(addon);

    trackEvent("pricing_addon_cta_clicked", {
      country: country.code,
      currency: addonPrice.currency,
      addon: addon.id,
      addon_name: addon.name,
      addon_key: addonPrice.addonKey || undefined,
      addon_price: addonPrice.amount ?? undefined,
      pricing_source: addonPrice.source,
      target: "addons",
    });

    if (typeof onSelectAddOn === "function") {
      onSelectAddOn({
        ...addon,
        addonKey: addonPrice.addonKey,
        amount: addonPrice.amount,
        currency: addonPrice.currency,
        pricingSource: addonPrice.source,
      });
      return;
    }

    openExternal(ADDONS_URL);
  };

  const planClasses = (plan) => {
    const planKey = String(plan.id ?? plan.name);
    const isSelected = activePlan === planKey;

    return `relative p-8 flex flex-col transition-all duration-200 ${
      isSelected
        ? "bg-[#1A46990D] border-2 border-blue-600 rounded-2xl shadow-lg"
        : "bg-white border border-gray-100 hover:border-gray-300 rounded-2xl"
    }`;
  };

  const billingSuffix = billing === "yearly" ? "/ year" : "/ month";

  return (
    <div className="bg-gray-50 py-16 px-6 font-sans">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
          Flexible Plans for Every Team
        </h1>

        <p className="mt-3 text-lg text-gray-500">
          Scale effortlessly from simple meetings to high-capacity enterprise webinars.
        </p>

        <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-2 px-3 py-1 bg-blue-50 text-[#1A4699] text-xs font-semibold rounded-full border border-blue-200">
          <span>
            Country: {country.name} ({country.code})
          </span>
          <span>•</span>
          <span>
            Currency: {currency}
            {currencySymbol ? ` (${currencySymbol})` : ""}
          </span>
        </div>
      </div>

      <div className="flex justify-center mb-10 mt-10">
        <div className="bg-white p-1 rounded-xl flex items-center space-x-2 border border-[#1A4699]">
          <button
            type="button"
            onClick={() => handleBillingChange("yearly")}
            className={`px-5 py-2 rounded-xl text-sm font-medium transition ${
              billing === "yearly" ? "bg-[#1A4699] text-white" : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            Yearly
          </button>

          <button
            type="button"
            onClick={() => handleBillingChange("monthly")}
            className={`px-5 py-2 rounded-xl text-sm font-medium transition ${
              billing === "monthly" ? "bg-[#1A4699] text-white" : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            Monthly
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {plansLoading ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
            <p className="text-gray-600">Loading current Konn3ct plans...</p>
          </div>
        ) : plansError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
            <p className="font-semibold text-red-700">Unable to load pricing plans</p>
            <p className="mt-2 text-sm text-red-600">{plansError}</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
            <p className="font-semibold text-gray-800">No pricing plans are currently available for {currency}.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {plans.map((plan) => {
              const planKey = String(plan.id ?? plan.name);
              const price = planPriceMap.get(planKey) || getPlanPrice(plan, billing);
              const enterpriseCustom =
                Boolean(price.isCustom) || (isEnterprisePlan(plan) && !price.available);
              const mostPopular = isMostPopularPlan(plan);
              const free = price.isFree;
              const features = Array.isArray(plan.features) ? plan.features : [];

              let priceLabel = "Not available";

              if (free) {
                priceLabel = "Free";
              } else if (price.available) {
                priceLabel = formatPlanAmount(price.amount);
              } else if (enterpriseCustom) {
                priceLabel = "Custom pricing";
              }

              const ctaLabel = enterpriseCustom ? "Contact Sales" : free ? "Free forever" : "Get Started";

              return (
                <motion.article
                  key={planKey}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handlePlanClick(plan)}
                  className={`${planClasses(plan)} cursor-pointer`}
                >
                  {mostPopular && (
                    <span className="absolute top-3 right-3 bg-blue-600 text-white px-3 py-1 text-xs rounded-full">
                      Most Popular
                    </span>
                  )}

                  <div>
                    <h2 className="text-lg font-bold uppercase text-gray-800 pr-20">{plan.name}</h2>

                    <p className="text-sm text-gray-500 mt-1 mb-4">{getPlanDescription(plan)}</p>

                    <h3
                      className={`font-bold mb-1 ${
                        free
                          ? "text-3xl text-green-600"
                          : price.available
                            ? "text-3xl text-[#1A4699]"
                            : "text-2xl text-gray-800"
                      }`}
                    >
                      {priceLabel}
                    </h3>

                    <p className="text-sm text-gray-500 mb-6">
                      {free
                        ? "No credit card required"
                        : price.available
                          ? billingSuffix
                          : enterpriseCustom
                            ? "Talk to our team"
                            : `${billing === "yearly" ? "Yearly" : "Monthly"} pricing is not currently returned by the API`}
                    </p>

                    <button
                      type="button"
                      disabled={!price.available && !enterpriseCustom}
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePlanCta(plan);
                      }}
                      className={`w-full py-2 rounded-lg font-semibold mb-6 transition ${
                        !price.available && !enterpriseCustom
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : free
                            ? "bg-green-700 hover:bg-green-800 text-white"
                            : enterpriseCustom
                              ? "bg-gray-900 hover:bg-gray-800 text-white"
                              : "bg-[#1A4699] hover:bg-blue-700 text-white"
                      }`}
                    >
                      {ctaLabel}
                    </button>

                    <FeatureList features={features} />
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>

      {addons.length > 0 && (
        <div className="max-w-7xl mx-auto mt-14 border rounded-2xl p-8 shadow-sm bg-white">
          <h3 className="text-lg font-bold mb-3 text-black">Usage-Based Add-ons</h3>

          <p className="text-sm text-gray-600 mb-6">Extras to boost your capacity and productivity</p>

          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {addons.map((addon, index) => {
              const Icon = getAddonIcon(addon);
              const addonPrice = resolveAddonPrice(addon);

              return (
                <motion.article
                  key={addon.id ?? `${addon.name}-${index}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="bg-white hover:bg-blue-50 border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md"
                  role="region"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-[#1A4699] text-white">
                      <Icon className="w-6 h-6" />
                    </div>

                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-gray-800">{addon.name}</h4>

                      <p className="text-sm mt-1 text-gray-500">
                        {addon.description || "Additional Konn3ct capability"}
                      </p>
                    </div>

                    <div className="text-right">
                      <div className="text-lg font-semibold text-[#1A4699] whitespace-nowrap">
                        {Number.isFinite(addonPrice.amount)
                          ? formatCurrency({
                              amount: addonPrice.amount,
                              currency: addonPrice.currency,
                              locale: addonPrice.locale,
                            })
                          : "Contact us"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => handleAddonCta(addon)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 bg-[#1A4699] text-white text-sm font-medium hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                    >
                      Addons
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-3 text-gray-800 text-lg font-semibold relative z-10 space-x-1 mt-14">
        <span className="inline-block h-10 w-6">
          <img src={LeafL} alt="" className="h-full object-contain" aria-hidden="true" />
        </span>

        <p className="text-center">Trusted by 15,000+ individuals and companies.</p>

        <span className="inline-block h-10 w-6">
          <img src={LeafR} alt="" className="h-full object-contain" aria-hidden="true" />
        </span>
      </div>
    </div>
  );
};

export default Kpricing;
